import React, { useMemo } from "react";

import { TransitionProposalPerformanceMetric } from "@fartherfinance/frontend/api/TransitionProposals/types";
import { PortfolioPlanId } from "@fartherfinance/frontend/api/Types";

import { ModelPortfolioTypeOptions } from "../constants";
import {
  DefaultListItemOption,
  ListItemOption,
  ModelPortfolioQueryParams,
} from "../types";
import TransitionProposalDetails from "@src/multiCustodian/components/TransitionProposals/TransitionProposalDetails/TransitionProposalDetails";
import AnimationContainer from "@src/sharedComponents/AnimationContainer/AnimationContainer";
import Conditional from "@src/sharedComponents/Conditional/Conditional";
import DataList from "@src/sharedComponents/DataList/DataList";
import Text from "@src/sharedComponents/Typography/Text";

interface PortfolioDataListProps {
  queryParams: ModelPortfolioQueryParams | undefined;
  selectedModelPortfolio: ListItemOption | undefined;
  listData: ListItemOption[];
  defaultListData: DefaultListItemOption[];
  searchTerm: string;
  isLoadingQuery: boolean;
  currentStackListItem: any;
  height?: number | string;
  handleOnClickDefaultItem: (item: DefaultListItemOption) => () => void;
  handleOnClickChildItem: (item: ListItemOption) => () => void;
  handleOnEndReached: (entries: IntersectionObserverEntry[]) => void;
  handleOnSearch: (term: string) => void;
  handleOnClearSearch: () => void;
  handleBackButtonClick: () => void;
  metricVisibilitySettings?: Record<string, boolean>;
  onToggleMetricVisibility: (
    key: TransitionProposalPerformanceMetric
  ) => () => void;
  showSearchClearButton?: boolean;
  onExitComplete?: () => void;
  disableMetricVisibilityToggle?: boolean;
  onError?: (hasError: boolean) => void;
}

const { CUSTOM_PORTFOLIOS, SHARED_BY_ADVISOR, FARTHER_LIST } =
  ModelPortfolioTypeOptions;

const PortfolioDataList: React.FC<PortfolioDataListProps> = ({
  queryParams,
  selectedModelPortfolio,
  listData,
  defaultListData,
  searchTerm,
  isLoadingQuery,
  currentStackListItem,
  handleOnClickDefaultItem,
  handleOnClickChildItem,
  handleOnEndReached,
  handleOnSearch,
  handleOnClearSearch,
  handleBackButtonClick,
  metricVisibilitySettings,
  onToggleMetricVisibility,
  showSearchClearButton = false,
  height = 500,
  disableMetricVisibilityToggle = false,
  onError,
}) => {
  const isDefaultList = useMemo(() => {
    return (
      queryParams === undefined ||
      queryParams.name === FARTHER_LIST ||
      defaultListData.length > 0
    );
  }, [queryParams, defaultListData.length]);

  const onSearch = useMemo(() => {
    return queryParams?.name === SHARED_BY_ADVISOR ? handleOnSearch : undefined;
  }, [queryParams, handleOnSearch]);

  return (
    <AnimationContainer
      contentKey={currentStackListItem?.id || queryParams?.name}
    >
      <Conditional condition={selectedModelPortfolio === undefined}>
        <DataList
          searchTerm={searchTerm}
          onSearch={onSearch}
          intersectionThreshold={1}
          onEndReached={handleOnEndReached}
          isLoading={isLoadingQuery && queryParams?.id !== null}
          onClear={handleOnClearSearch}
          height={height}
          showBackButton={false}
          onBackButtonClick={handleBackButtonClick}
          showSearchClearButton={showSearchClearButton}
        >
          {isDefaultList
            ? defaultListData.map((item) => (
                <DataList.DataListItem
                  key={item.title}
                  title={item.title}
                  subTitle={item.subTitle}
                  iconType={item.type === CUSTOM_PORTFOLIOS ? "add" : "caret"}
                  leftComponent={
                    item.subTitle === undefined ? (
                      <Text>{item.title}</Text>
                    ) : undefined
                  }
                  onClick={handleOnClickDefaultItem(item)}
                />
              ))
            : listData.map((item) => (
                <DataList.DataListItem
                  key={item.title}
                  title={item.title}
                  onClick={handleOnClickChildItem(item)}
                />
              ))}
        </DataList>
      </Conditional>
      <Conditional condition={selectedModelPortfolio !== undefined}>
        <TransitionProposalDetails
          portfolioPlanId={selectedModelPortfolio?.id as PortfolioPlanId}
          entries={selectedModelPortfolio?.securityEntries}
          metricVisibilitySettings={metricVisibilitySettings}
          onToggleMetricVisibility={onToggleMetricVisibility}
          disableMetricVisibilityToggle={disableMetricVisibilityToggle}
          onError={onError}
        />
      </Conditional>
    </AnimationContainer>
  );
};

export default PortfolioDataList;
